import { ErrorBoundary } from 'shared-services/error-service';

import React, { Component } from 'react';
import { Router, Route, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import customProtocolCheck from 'custom-protocol-detection';

import { fetchAuthDetails, setTeamUrl } from './actions/auth';

import * as userStorageService from './services/user-storage-service';
import { browserHistory } from './services/history';
import { redirectToTeam } from './services/redirect-service';
import { getTeamUrl } from './services/store-service';
import { redirectToLanding } from './globals-service';
import { isDev } from 'services/env-service';
import { getTeamUrlFromUrl } from './utils/url';
import store from './store';
import { trackPageView, startTrackSession, stopTrackSession } from './services/events-tracker-service';
import * as ElectronService from './services/electron-service';
import { initAppUpdate } from './services/app-update-service';

import PageProject from 'lazy/LazyProject';
import Signup from 'lazy/LazySignup';
import Settings from 'lazy/LazySettings';
import FilePreview from 'lazy/LazyFilePreview';
import LazyGeneric from 'lazy/LazyGeneric';
import PublicPage from 'lazy/LazyPublicPage';
import Dashboard from 'lazy/LazyDashboard';
import Overview from 'lazy/LazyOverview';
import Goals from 'lazy/LazyGoals';
import Workloads from 'lazy/LazyWorkloads';
import User from 'lazy/LazyUser';
import PageCalendar from 'lazy/LazyPageCalendar';
import PageAllTasks from 'lazy/LazyPageAllTasks';
import Subteam from 'lazy/LazySubteam';

import AppError from './app-error';
import AppWrap from 'components/app-wrap';
import AppRoot from 'components/app-root';
import AuthFlow from 'components/auth-flow';
import Redirect from 'components/Redirect';
import Gate from 'pages/gate';

import Maintenance from 'components/maintenance';

const MeetingEmbedApp = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "MeetingEmbedApp"*/ './pages/MeetingEmbedApp')} />;
const Authorize = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "Authorize"*/ './pages/authorize')} />;
const AcceptInvite = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "AcceptInvite"*/ './pages/accept-invite')} />;
const ResetPassword = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "ResetPassword"*/ './pages/reset-password')} />;
const Inbox = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "Inbox"*/ './pages/Inbox')} />;
const ShortLink = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "ShortLinkChunk"*/ './components/short-link')} />;
const ServiceAuthToken = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "ServiceAuthTokenChunk"*/ './components/service-auth-token')} />;
const DocMobile = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "DocMobileChunk"*/ './components/doc-mobile')} />;
const GoogleDriveShare = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "GoogleDriveShareChunk"*/ './components/google-drive-share')} />;
const ExternalAuth = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "ExternalAuthChunk"*/ './components/external-auth')} />;
const ExternalRedirect = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "ExternalRedirectChunk"*/ './components/external-redirect')} />;
const GoogleSSORedirect = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "GoogleSSORedirectChunk"*/ './components/google-sso-redirect')} />;
const MicrosoftSSORedirect = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "MicrosoftSSORedirectChunk"*/ './components/MicrosoftSSORedirect')} />;
const TokenLogin = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "TokenLoginChunk"*/ './components/token-login')} />;
const ElectronHelperBrowser = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "ElectronHelperBrowserChunk"*/ './components/electron-helper-browser')} />;
const TeamSelectorPage = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "TeamSelectorPageChunk"*/ './components/team-selector-page')} />;
const Icons = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "IconsChunk"*/ './components/icons')} />;
const StyleGuide = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "StyleGuideChunk"*/ './pages/StyleGuide')} />;
const FullscreenChat = props => <LazyGeneric {...props} importFn={() => import(/* webpackChunkName: "FullscreenChatChunk"*/ './components/FullscreenChat')} />;

initAppUpdate();

const CUSTOM_PROTOCOL_NO_REDIRECT = ['/auth-token', '/auth-service', '/l/', '/public'];

const urlTasks = () => {
    // Don't redirect on dev
    if (isDev()) {
        return;
    }

    if (!getTeamUrlFromUrl() && (!window.location.pathname || window.location.pathname === '/') && window.location.search === '?ref=producthunt') {
        redirectToLanding({ keepSearch: true });
        return;
    }

    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        setTimeout(() => {
            if (!CUSTOM_PROTOCOL_NO_REDIRECT.some(url => window.location.pathname.indexOf(url) === 0)) {
                customProtocolCheck(`niftypm://${window.location.pathname}`, null, null, () => {});
            }
        }, 3000);
    }
}

urlTasks();

// datadogRum.init({
//     applicationId: '68690d5b-1b53-46ff-8669-0f2b8fb59a6c',
//     clientToken: 'pub2b0ad9a588831033defb344629ccd068',
//     site: 'datadoghq.com',
//     service: isElectron() ? 'nifty-desktop' : 'nifty-web',
//     version: process.env.REACT_APP_VERSION,
//     env: getEnv(),
//     sampleRate: 100,
//     trackInteractions: true
// });

const detectScrollbarWidth = () => {
    const scrollDiv = document.createElement("div");

    scrollDiv.style = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;';
    document.body.appendChild(scrollDiv);

    // Get the scrollbar width
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

    // Delete the DIV 
    document.body.removeChild(scrollDiv);

    return scrollbarWidth;
}

let scrollbarHasWidth = false;

class App extends Component {
    constructor(props) {
        super(props);

        // if (isElectron()) {
        //     browserHistory.push(window.location.pathname);
        // }

        startTrackSession('App');

        fetchAuthDetails()
            .then(authDetails => {
                // Don't redirect on dev
                if (isDev()) {
                    return;
                }

                if (getTeamUrlFromUrl()) {
                    setTeamUrl(getTeamUrlFromUrl());

                    return;
                }

                redirectToTeam(getTeamUrl());

                // if (!getTeamUrlFromUrl() && getTeamUrl()) {
                //     setTeamUrl(getTeamUrl());
                // }
            });

        setInterval(() => {
            try {
                if (detectScrollbarWidth() && !scrollbarHasWidth) {
                    scrollbarHasWidth = true;
                    document.body.classList.add('scrollbar-have-width');
                } else if (scrollbarHasWidth && !detectScrollbarWidth()) {
                    scrollbarHasWidth = false;
                    document.body.classList.remove('scrollbar-have-width');
                }
            } catch (e) {}
        }, 5000);


        window.addEventListener('beforeunload', () => {
            userStorageService.set('lastSession', {
                pathname: browserHistory.location.pathname,
                timestamp: Date.now()
            });

            stopTrackSession('App');

            ElectronService.emit('logout');
        });
    }

    handleRouteUpdate = () => {
        trackPageView(window.location.pathname);
    }

    componentWillUnmount() {
        stopTrackSession('App');
    }

    render() {
        return (
            <ErrorBoundary FallbackComponent={ AppError }>
                <Provider store={ store }>
                    <Router history={ browserHistory } onUpdate={ this.handleRouteUpdate }>
                        <AppWrap history={ browserHistory }>
                            <Switch>
                                <Route path="/token" />

                                <Route path="/icons">
                                    <Icons />
                                </Route>

                                <Route path="/styleguide">
                                    <StyleGuide />
                                </Route>

                                <Route path="/maintenance">
                                    <Maintenance />
                                </Route>

                                <Route path="/authorize">
                                    <Authorize />
                                </Route>

                                <Route path="/doc-mobile">
                                    <DocMobile />
                                </Route>

                                <Route path="/l/:token">
                                    <ShortLink />
                                </Route>

                                <Route path="/auth-token/:service">
                                    <ServiceAuthToken />
                                </Route>

                                <Route path="/google-drive-share">
                                    <GoogleDriveShare />
                                </Route>

                                <Route path="/external-redirect/:provider">
                                    <ExternalRedirect />
                                </Route>

                                <Route path="/external-auth">
                                    <ExternalAuth />
                                </Route>

                                <Route path="/electron-helper-browser">
                                    <ElectronHelperBrowser />
                                </Route>

                                <Route path="/auth-service/google">
                                    <GoogleSSORedirect />
                                </Route>

                                <Route path="/auth-service/microsoft">
                                    <MicrosoftSSORedirect />
                                </Route>

                                <Route path="/login-token/:token/:teamToken?">
                                    <TokenLogin />
                                </Route>

                                <Route path="/form-preview/:formId">
                                    <PublicPage type="formPreview" />
                                </Route>

                                <Route path="/form/:publicToken">
                                    <PublicPage type="form" />
                                </Route>

                                <Route path="/public/:publicToken">
                                    <PublicPage />
                                </Route>

                                <Route path="/">
                                    <AppRoot>
                                        <Switch>
                                            <Route path="/teams">
                                                <TeamSelectorPage />
                                            </Route>

                                            <Route path="/document/:documentToken">
                                                <PublicPage />
                                            </Route>

                                            <Route path="/auth/:stage">
                                                <AuthFlow />
                                            </Route>

                                            <Route path="/login">
                                                <AuthFlow />
                                            </Route>

                                            <Route path="/accept_invite">
                                                <AcceptInvite />
                                            </Route>

                                            <Route path="/reset_password">
                                                <ResetPassword />
                                            </Route>

                                            <Route path="/signup">
                                                <Signup />
                                            </Route>

                                            <Route path="/">
                                                <Gate>
                                                    <Switch>
                                                        <Route path="/psignup" sidebarHidden={true}>
                                                            <Signup inGate={true} />
                                                        </Route>

                                                        <Route path="/dashboard">
                                                            <Dashboard />
                                                        </Route>

                                                        <Route path="/dashboard-new">
                                                            <Dashboard afterSignup={true} />
                                                        </Route>

                                                        <Route path="/chat/:conversationId/message/:messageId?" exact={true}>
                                                            <FullscreenChat />
                                                        </Route>

                                                        <Route path="/chat/:conversationId">
                                                            <FullscreenChat />
                                                        </Route>

                                                        <Route path="/overview" pageId="overview">
                                                            <Overview />
                                                        </Route>

                                                        <Route path="/goals/:goalId?" pageId="goals">
                                                            <Goals />
                                                        </Route>

                                                        <Route path="/workloads/:type?">
                                                            <Workloads />
                                                        </Route>

                                                        <Route path="/edit-project/:projectId">
                                                            <Dashboard autoShowModal="EDIT_PROJECT" />
                                                        </Route>

                                                        <Route path="/create-team">
                                                            <Dashboard autoShowModal="CREATE_TEAM" />
                                                        </Route>

                                                        <Route path="/create-project">
                                                            <Dashboard autoShowModal="CREATE_PROJECT" />
                                                        </Route>

                                                        <Route path="/upgrade">
                                                            <Dashboard autoShowModal="SWITCH_PLAN" />
                                                        </Route>

                                                        <Route path="/activate">
                                                            <Dashboard autoShowModal="SWITCH_PLAN" modalProps={{ step: 'billing' }} />
                                                        </Route>

                                                        <Route path="/update-billing">
                                                            <Dashboard autoShowModal="EDIT_BILLING" modalProps={{ step: 'billing' }} />
                                                        </Route>

                                                        <Route path="/template-center">
                                                            <Dashboard autoShowModal="TEMPLATE_CENTER" />
                                                        </Route>

                                                        <Route path="/template/:token" children={({ match }) => (
                                                            <Dashboard
                                                                autoShowModal="TEMPLATE_CENTER"
                                                                modalProps={{
                                                                    initialResource: 'import',
                                                                    importToken: match.params.token
                                                                }}
                                                            />
                                                        )} />

                                                        <Route path="/files/:fileId/comments">
                                                            <FilePreview showComments={true} />
                                                        </Route>

                                                        <Route path="/files/:fileId">
                                                            <FilePreview />
                                                        </Route>

                                                        <Route path="/settings" name="settings" sidebarHidden={true}>
                                                            <Settings />
                                                        </Route>

                                                        <Route path="/portfolio/:subteamId">
                                                            <Subteam />
                                                        </Route>

                                                        <Route path="/my">
                                                            <User />
                                                        </Route>

                                                        <Route path="/inbox">
                                                            <Inbox />
                                                        </Route>

                                                        <Route path="/tasks/:taskId?/:subtaskId?">
                                                            <PageAllTasks />
                                                        </Route>

                                                        <Route path="/calendar">
                                                            <PageCalendar />
                                                        </Route>

                                                        <Route path="/webex">
                                                            <MeetingEmbedApp provider="webex" />
                                                        </Route>

                                                        <Route path="/zoom">
                                                            <MeetingEmbedApp provider="zoom" />
                                                        </Route>

                                                        <Route path="/:projectId">
                                                            <PageProject />
                                                        </Route>

                                                        <Redirect from="/" to="/my" />
                                                    </Switch>
                                                </Gate>
                                            </Route>
                                        </Switch>
                                    </AppRoot>
                                </Route>

                                <Redirect from="*" to="/" />
                            </Switch>
                        </AppWrap>
                    </Router>
                </Provider>
            </ErrorBoundary>
        );
    }
}

export default App;
